import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './PopularDestinations.css';

// Popular destinations (default)
const POPULAR_DESTINATIONS = [
  {
    name: 'London',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&h=600&fit=crop',
    searchTerms: ['London', 'london', 'LONDON'],
    isLarge: true
  },
  {
    name: 'Manchester',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop',
    searchTerms: ['Manchester', 'manchester', 'MANCHESTER'],
    isLarge: true
  },
  {
    name: 'Liverpool',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=600&h=400&fit=crop',
    searchTerms: ['Liverpool', 'liverpool', 'LIVERPOOL'],
    isLarge: false
  },
  {
    name: 'Birmingham',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&h=400&fit=crop',
    searchTerms: ['Birmingham', 'birmingham', 'BIRMINGHAM'],
    isLarge: false
  },
  {
    name: 'Glasgow',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=600&h=400&fit=crop',
    searchTerms: ['Glasgow', 'glasgow', 'GLASGOW'],
    isLarge: false
  },
  {
    name: 'Edinburgh',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&h=400&fit=crop',
    searchTerms: ['Edinburgh', 'edinburgh', 'EDINBURGH'],
    isLarge: false
  }
];

// Food & Bourbon destinations
const FOOD_BOURBON_DESTINATIONS = [
  {
    name: 'York',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop',
    searchTerms: ['York', 'york', 'YORK'],
    isLarge: true
  },
  {
    name: 'Brighton & Hove',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop',
    searchTerms: ['Brighton', 'brighton', 'BRIGHTON', 'Hove', 'hove'],
    isLarge: true
  },
  {
    name: 'Leeds',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1596436889106-be35e843f974?w=600&h=400&fit=crop',
    searchTerms: ['Leeds', 'leeds', 'LEEDS'],
    isLarge: false
  },
  {
    name: 'Birmingham',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&h=400&fit=crop',
    searchTerms: ['Birmingham', 'birmingham', 'BIRMINGHAM'],
    isLarge: false
  },
  {
    name: 'Manchester',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=600&h=400&fit=crop',
    searchTerms: ['Manchester', 'manchester', 'MANCHESTER'],
    isLarge: false
  },
  {
    name: 'Liverpool',
    country: 'United Kingdom',
    image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e4f2?w=600&h=400&fit=crop',
    searchTerms: ['Liverpool', 'liverpool', 'LIVERPOOL'],
    isLarge: false
  }
];

// Filter to destinations mapping
const FILTER_DESTINATIONS = {
  'Popular': POPULAR_DESTINATIONS,
  'Explore United Kingdom': POPULAR_DESTINATIONS,
  'Food & Bourbon': FOOD_BOURBON_DESTINATIONS,
  'Shopping': POPULAR_DESTINATIONS,
};

const mapFilterToDestinations = (filterKey) => {
  const filterDestinations = FILTER_DESTINATIONS[filterKey] || POPULAR_DESTINATIONS;
  return filterDestinations.map((dest) => ({
    id: `${filterKey}-${dest.name}`,
    cityId: '',
    name: dest.name,
    country: dest.country,
    location: `${dest.name}, ${dest.country}`,
    image: dest.image,
    hotels: 0,
    g2City: null,
    isLarge: dest.isLarge,
  }));
};

const PopularDestinations = ({ onSearch }) => {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('Popular');
  const destinations = useMemo(
    () => mapFilterToDestinations(selectedFilter),
    [selectedFilter]
  );

  const handleDestinationClick = (destination) => {
    // Set default dates (tomorrow as check-in, 1 night stay)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const checkOut = new Date(tomorrow);
    checkOut.setDate(checkOut.getDate() + 1);
    
    const searchParams = {
      location: destination.name,
      checkIn: tomorrow.toISOString().split('T')[0],
      checkOut: checkOut.toISOString().split('T')[0],
      nights: 1,
      adults: 1,
      children: 0,
      guests: 1,
      // Include cityId if available
      ...(destination.cityId && { cityId: destination.cityId }),
    };
    
    console.log('🔍 Navigating to hotels for destination:', searchParams);
    
    // Navigate to hotels page with search params
    const queryString = new URLSearchParams({
      location: searchParams.location,
      checkIn: searchParams.checkIn,
      checkOut: searchParams.checkOut,
      nights: searchParams.nights.toString(),
      adults: searchParams.adults.toString(),
      children: searchParams.children.toString(),
      guests: searchParams.guests.toString(),
      searchTarget: 'city',
      ...(searchParams.cityId && { cityId: searchParams.cityId }),
    }).toString();
    
    navigate(`/hotels?${queryString}`);
    window.scrollTo(0, 0);
  };

  const filters = [
    'Popular',
    'Explore United Kingdom',
    'Food & Bourbon',
    'Shopping',
  ];

  return (
    <section className="popular-destinations" id="destinations">
      <div className="popular-destinations__container">
        <div className="popular-destinations__header">
          <h2 className="popular-destinations__title">Trending destinations</h2>
          <p className="popular-destinations__subtitle">
            Popular destinations to kickstart your planning
          </p>
        </div>
        
        <div className="popular-destinations__filters">
          {filters.map(filter => (
            <button
              key={filter}
              className={`popular-destinations__filter ${selectedFilter === filter ? 'popular-destinations__filter--active' : ''}`}
              onClick={() => setSelectedFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>
        
        <div className="popular-destinations__grid">
          {destinations.map((destination) => (
            <div
              key={destination.id}
              className={`destination-card ${destination.isLarge ? 'destination-card--large' : ''}`}
              onClick={() => handleDestinationClick(destination)}
            >
              <div className="destination-card__image-wrapper">
                <img
                  src={destination.image}
                  alt={destination.name}
                  className="destination-card__image"
                  onError={(e) => {
                    e.target.src =
                      'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400';
                  }}
                />
                <div className="destination-card__overlay">
                  <div className="destination-card__content">
                    <h3 className="destination-card__name">
                      {destination.name}
                      <span className="destination-card__flag">🇬🇧</span>
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PopularDestinations;
